import React, { ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Ava, Bx, Collapse, IconAva, Typo } from '@curry-group/mui-curcuma';
import { ListAvatarColors, getDetailRoute, getTypeAlias, hashCode, resolveTimestamp } from '../../../helper';
import { ICommunicationListItem } from '../../../model/communication/Communication';
import { IContainerConfig } from '../../../model/configuration';
import { IListItem } from '../../../model/list/ListItem';
import { ThingTypes, transformForList } from '../../../model/ryve/Thing';
import { AppFrameSidebar } from '../../frame/sidebar';
import { AddSearch, IAddSearchProps } from '../../list-items/add-search';
import { SidebarListItem, SidebarListItemEmpty, SidebarListItemSkeleton } from '../../list-items/sidebar';
import { ListSidebar, SidebarWrapper } from '../../sidebar';
import { ParticipationRequestStatus, ParticipationRequestType } from '../../../model/communication/Participant';
import { MobileHeader } from '../../header/mobile';
import { useIsMobile } from '../../../helper/hooks/isMobile';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlaskPotion, faMessageBot } from '@fortawesome/pro-light-svg-icons';
import { faTriangle } from '@fortawesome/pro-solid-svg-icons';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { userprofileMemorylistRequestAction } from '../../../data/actions/foundation';
import { AvatarWithBadge } from '../../user-profile/avatarWithBadge';
import { AvatarWithDeactivatedBadge } from '../../deactivated-icon/avatar-badge';

const useStyles = makeStyles(theme => ({
  collector: {
    padding: '24px 24px 12px 24px',
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer'
  },
  collapse: {
    boxShadow: '0 2px 2px 0 rgba(0,0,0,.15)'
  },
  collapsed: {
    borderBottom: '1px solid rgba(0,0,0,.15)'
  },
  collectorbottom: {
    marginTop: '2px'
  },
  showMore: {
    padding: '12px 24px',
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
    borderTop: '1px solid rgba(0,0,0,.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main,
    fontSize: 13,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,.03)'
    }
  }
}));

export interface ICommunicationSidebarProps extends Omit<IAddSearchProps, 'maxHeight'> {
  items?: ICommunicationListItem[];
  working?: boolean;
  isMobile?: boolean;
  /** URL of the container the sidebar belongs to, e.g. "/bot" or "/coop/chat". */
  containerUrl?: string;
  /** Bot rubric: permanent bot item on top, saved sessions below, nothing else. */
  isBotContainer?: boolean;
}

export const ContainerSidebar: React.FC<{ config: IContainerConfig }> = ({ config }) => {
  const [searchInput, setSearchInput] = useState('');

  const isMobile = useIsMobile();
  const communications = useSelector(state => state.communication.communications);
  const communicationsWorking = useSelector(state => state.communication.communicationsWorking);

  const match = useRouteMatch();
  const history = useHistory();

  // In the bot rubric there is nothing to create: a session starts by chatting
  // with the permanent bot item and becomes a list entry by saving it. Searching
  // the saved sessions is part of that rubric, so it is on regardless of
  // SidebarSearch.
  const isBotContainer = !!config.botChatEnabled;

  return (
    <AppFrameSidebar isMobile={isMobile}>
      {isMobile && <MobileHeader type="default" />}
      <CommunicationSidebar
        working={communicationsWorking}
        containerUrl={match.url}
        isBotContainer={isBotContainer}
        items={
          searchInput
            ? communications?.filter(item => {
                let title = item.content.title;
                if (item.content?.partner?.content?.fullName) {
                  title = item.content.partner.content.fullName;
                }
                return title?.toLowerCase().includes(searchInput.toLowerCase());
              }) || []
            : communications
        }
        addButtonClicked={
          !isBotContainer && config.sidebarCreate && config.canCreateContent
            ? () => {
                history.push(match.url + '/flow');
              }
            : undefined
        }
        searchEnabled={config.sidebarSearch || isBotContainer}
        caption={isBotContainer ? 'Bot Unterhaltungen' : undefined}
        searchInput={searchInput}
        searchInputChange={setSearchInput}
        searchPlaceholder={isBotContainer ? 'Gespeicherte Sessions suchen' : undefined}
        addButtonCaption={config.createContentCaption}
      />
    </AppFrameSidebar>
  );
};

export const ConnectedCommunicationSidebarListItem: React.FC<{
  item: IListItem;
  communication?: ICommunicationListItem;
  isMobile?: boolean;
  colorObject?: { color: string; bg: string };
  appconfig?: any;
  active?: boolean;
}> = ({
  item,
  communication,
  isMobile,
  colorObject,
  appconfig,
  active
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const unreadMessages = useSelector(state => state.communication.numUnreadMessages?.[item.typeId]?.[item._id] || 0);
  const isBotChatItem = !!item.isBotChat || !!item.title?.endsWith(' - Bot');
  let baseAvatar;
  if (isBotChatItem) {
    baseAvatar = (
      <Ava
        size="medium"
        src={undefined}
        style={{ color: '#1565c0', background: '#e3f2fd', fontWeight: 500 }}
      >
        <FontAwesomeIcon icon={faMessageBot} />
      </Ava>
    );
  } else if (item?.avatar?.type === 'user') {
    baseAvatar = <Ava size="medium" src={undefined} style={{color: colorObject?.color, background: colorObject?.bg, fontWeight: 500}}>{item.avatar.abbreviation}</Ava>;
  } else {
    baseAvatar = <IconAva variant="circular" style={{color: colorObject?.color, background: colorObject?.bg, fontWeight: 500}} children={<FontAwesomeIcon icon={faFlaskPotion} />} size="medium" />;
  }

  if (!isBotChatItem && (item.avatar.type === 'image' || item.avatar.type === 'user') && item.avatar.image) {
    if (item.typeId === ThingTypes.OneToOne) {
      if (item.avatar.image) {
        baseAvatar = <Ava size="medium" src={item.avatar.image} style={{fontWeight: 500}}>{item.avatar.abbreviation}</Ava>;
      } else {
        baseAvatar = <Ava size="medium" src={undefined} style={{color: colorObject?.color, background: colorObject?.bg, fontWeight: 500}}>{item.avatar.abbreviation}</Ava>;
      }
    } else {
      if (item.avatar.image) {
        baseAvatar = <Ava size="medium" src={item.avatar.image} style={{fontWeight: 500}}>{<FontAwesomeIcon icon={faFlaskPotion} />}</Ava>;
      } else {
        baseAvatar = <Ava size="medium" src={undefined} style={{color: colorObject?.color, background: colorObject?.bg, fontWeight: 500}}>{<FontAwesomeIcon icon={faFlaskPotion} />}</Ava>;
      }
    }
  }

  const userStatusAvatar = <AvatarWithBadge userStatus={communication?.content?.partner?.content?.generalUserStatus} avatar={baseAvatar} />;
  const avatar = item.isDeactivated && item.typeId !== ThingTypes.OneToOne
    ? <AvatarWithDeactivatedBadge avatar={userStatusAvatar} deactivated />
    : userStatusAvatar;

  let badgeContent: string | number | ReactNode = unreadMessages;
  let badgeColor: 'primary' | 'secondary' | 'default' = 'primary';
  if (communication) {
    if (communication.content.status === ParticipationRequestStatus.PENDING) {
      badgeColor = 'default';
      if (communication.content.type === ParticipationRequestType.INVITE) {
        badgeContent = 'Einladung';
      } else {
        badgeContent = 'Anfrage';
      }
    } else if (badgeContent === 0) {
      if (communication?.content?.partner?.content?.status === ParticipationRequestStatus.PENDING) {
        badgeColor = 'default';
        badgeContent = 'Anfrage';
      }
    }
  }

  return (
    <SidebarListItem
      isMobile={isMobile}
      subtitle={item.type}
      to={getDetailRoute(item, appconfig || {}, location)}
      title={item.title}
      organisation={item?.__original?.content?.partner?.content?.organisation ?? ''}
      avatar={avatar}
      isOneToOne={item.typeId === ThingTypes.OneToOne}
      isPublic={!item.isPrivate}
      isHidden={item.isHidden}
      isDeactivated={item.isDeactivated}
      timestamp={item.timestamp ? resolveTimestamp(parseInt(item.timestamp)) : ''}
      badgeContent={badgeContent}
      badgeColor={badgeColor}
      booked={item.booked}
      memoryClick={() => dispatch(userprofileMemorylistRequestAction({
        memoryItem: { elementId: item._id || '', type: 'thing', typeId: item.typeId || ''},
        type: item.booked ? 'DELETE' : 'POST'
      }))}
      active={active}
    />
  );
};

const SidebarAddSearch: React.FC<Omit<Omit<IAddSearchProps, 'searchActive'>, 'setSearchActive'>> = props => {
  const [searchActive, setSearchActive] = useState(false);

  if (!!props.addButtonClicked || props.searchEnabled)
    return (
      <AddSearch
        placedInSidebar
        isMobile={props.isMobile}
        maxHeight={props.maxHeight}
        addButtonClicked={props.addButtonClicked}
        addButtonCaption={props.addButtonCaption || 'erstellen'}
        caption={props.caption}
        searchActive={searchActive}
        searchInput={props.searchInput}
        searchInputChange={props.searchInputChange}
        searchEnabled={props.searchEnabled}
        searchPlaceholder={props.searchPlaceholder || 'Suchen'}
        setSearchActive={setSearchActive}
      />
    );
  return <></>;
};

export const CommunicationSidebar: React.FC<Omit<Omit<ICommunicationSidebarProps, 'searchActive'>, 'setSearchActive'>> = ({
  working,
  items,
  addButtonClicked,
  addButtonCaption,
  searchEnabled,
  searchInput,
  searchInputChange,
  searchPlaceholder,
  caption,
  containerUrl,
  isBotContainer
}) => {
  // Use useLocation (not useHistory().location) so the component re-renders on
  // route changes — useHistory's location is read once and goes stale, which
  // made the bot-chat and previously opened saved-chat sidebar items light up
  // at the same time when the user switched between them.
  const location = useLocation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const appconfig = useSelector(state => state.foundation.appconfig);
  const parentId = useSelector(state => state.detail?.item?.content?.parentThing?.[0]?.elementId);
  const detailId = useSelector(state => state.detail?.item?._id);
  const memoryList = useSelector(state => state.foundation?.profile?.memoryList);
  // Feature switch: the bot-chat item + saved-bot-chat section are only shown
  // when the active container config enables it (default off).
  const botChatEnabled = useSelector(state => !!state.foundation?.activeConfig?.botChatEnabled);

  const pathname = location.pathname;
  const isBotChatActive = pathname.endsWith('/botchat');
  const isCommunicationDashboarrd = pathname === '/coop/groups-projects' || pathname === '/coop/chat';
  // The Medtec Online-Bot item and the saved-session list belong to the bot
  // rubric, which is identified by its container config (botChatEnabled) rather
  // than by a hardcoded path — the bot chat is no longer bound to /coop/chat.
  const botContainer = isBotContainer ?? botChatEnabled;
  const botChatUrl = (containerUrl || '') + '/botchat';

  const memoryIds = memoryList?.map(m => m.elementId);

  const maxHeightSidebarSearch = !isMobile ? 81 - 1 : undefined; // -1 due to border from ListWrapper

  const _searchEnabled = !!(searchEnabled && searchInputChange);

  // Saved sessions live in the bot rubric, but the type mapping that builds detail
  // routes belongs to the chat container — without this they would link to
  // /coop/chat/chat/<alias>, where the bot UI is switched off.
  const oneToOneAlias = getTypeAlias(appconfig || {}, ThingTypes.OneToOne);
  const withBotRoute = (item: IListItem): IListItem =>
    botContainer && containerUrl && item.alias
      ? { ...item, to: `${containerUrl}/${oneToOneAlias}/${item.alias}` }
      : item;

  function renderItem(item: IListItem, index: number) {
    try {
      const hash = item?.avatar?.abbreviation
        ? hashCode(item?.avatar?.abbreviation ?? '')
        : hashCode(item?.title.slice(0, 2) ?? '');
      const colorIndex = hash % 4;
      const colorObject = ListAvatarColors[colorIndex];
      // Regular items only highlight when we're actually on a detail page —
      // not on the dashboard and not on the fresh /botchat route. Without the
      // /botchat exclusion, the previously opened saved chat (still in
      // state.detail.item) keeps highlighting even after navigation to the
      // fresh bot session.
      return <ConnectedCommunicationSidebarListItem
        key={item._id}
        appconfig={appconfig}
        isMobile={isMobile}
        item={withBotRoute(item)}
        communication={items?.find(i => i._id === item._id)}
        colorObject={colorObject}
        active={!isCommunicationDashboarrd && !isBotChatActive && (item._id === parentId || item._id === detailId)}
      />;
    } catch (e) {
      return <></>;
    }
  }

  const itemsForList = transformForList(
    (items || []).map(item => {
      return {
        _source: item,
        _id: item._id,
        _score: 0,
        booked: !!item?._id && memoryIds?.includes(item?._id)
      };
    })
  );

  const [memorizedExpanded, setMemorizedExpanded] = useState(true);
  const [defaultExpanded, setDefaultExpanded] = useState(true);

  // Each expandable section starts collapsed-to-3: even when the section's
  // Collapse panel is expanded, only the first 3 items render until the user
  // clicks the "weitere anzeigen" button. These flags only matter when the
  // section actually has more than COLLAPSED_PREVIEW_COUNT items.
  const COLLAPSED_PREVIEW_COUNT = 3;
  // The saved sessions are the bot rubric's main list, so it previews more.
  const SAVED_BOTS_PREVIEW_COUNT = 5;
  const [savedBotsShowAll, setSavedBotsShowAll] = useState(false);
  const [memorizedShowAll, setMemorizedShowAll] = useState(false);
  const [defaultShowAll, setDefaultShowAll] = useState(false);

  // In the bot rubric the backend already returns saved bot chats only, so every
  // item is a session. Elsewhere bot chats are filtered out server-side and the
  // " - Bot" title suffix still catches legacy saves.
  const isBotItem = (item: IListItem) => !!item.isBotChat || !!item.title?.endsWith(' - Bot');
  const botSavedItems = botContainer ? itemsForList : itemsForList.filter(isBotItem);
  const nonBotItems = botContainer ? [] : itemsForList.filter(item => !isBotItem(item));
  const memorizedItems = !searchInput ? nonBotItems.filter(item => item.booked) : nonBotItems;
  const defaultItems = !searchInput ? nonBotItems.filter(item => !item.booked) : undefined;

  // "Gespeicherte Bots" starts open and previews the newest sessions; a search
  // opens it too and lists every match, so searching works on the saved sessions.
  const [savedBotsExpanded, setSavedBotsExpanded] = useState(true);
  const savedBotsOpen = savedBotsExpanded || !!searchInput;
  const savedBotsUnsorted = botContainer ? botSavedItems : searchInput ? [] : botSavedItems;
  // Newest first, so the preview really shows the most recent sessions no matter
  // how the list pipeline ordered them.
  const savedBotsVisible = [...savedBotsUnsorted].sort(
    (a, b) => (parseInt(b.timestamp || '0', 10) || 0) - (parseInt(a.timestamp || '0', 10) || 0)
  );

  const botSidebarItem: IListItem = {
    _id: '__bot_chat__',
    __original: null,
    typeId: ThingTypes.OneToOne,
    title: 'Medtec Online-Bot',
    type: 'Chat',
    avatar: { type: 'icon' }
  };

  return (
    <SidebarWrapper>
      <>
      <ListSidebar<IListItem>
        working={working}
        items={undefined}
        renderItem={renderItem}
        top={
          !!addButtonClicked || _searchEnabled ? (
            <SidebarAddSearch
              isMobile={isMobile}
              placedInSidebar
              maxHeight={maxHeightSidebarSearch}
              addButtonClicked={addButtonClicked}
              addButtonCaption={addButtonCaption || 'erstellen'}
              caption={caption}
              searchInput={searchInput}
              searchInputChange={searchInputChange}
              searchEnabled={_searchEnabled}
              searchPlaceholder={searchPlaceholder || 'Suchen'}
            />
          ) : (
            <></>
          )
        }
      ></ListSidebar>
      {/* Permanent Bot Chat Item (bot rubric only) */}
      {!searchInput && botContainer && (
        <ListSidebar<IListItem>
          working={false}
          items={[botSidebarItem]}
          renderItem={() => (
            <SidebarListItem
              key="__bot_chat__"
              isMobile={isMobile}
              title="Medtec Online-Bot"
              subtitle="Chat"
              organisation="MTO Medtec Online GmbH"
              to={botChatUrl}
              avatar={
                <Ava
                  size="medium"
                  src={undefined}
                  style={{ color: '#1565c0', background: '#e3f2fd', fontWeight: 500 }}
                >
                  <FontAwesomeIcon icon={faMessageBot} />
                </Ava>
              }
              isOneToOne
              timestamp=""
              active={isBotChatActive}
            />
          )}
          top={<></>}
        />
      )}
      {/* Gespeicherte Bots section (bot rubric only) */}
      {!!savedBotsVisible.length && (
        <Bx display="flex" justifyContent="flex-start" alignItems="center" className={clsx(classes.collector)} onClick={() => {
          // Closing the section also resets the show-all preview so the next
          // time it's opened the user lands on the 3-item teaser again.
          if (savedBotsExpanded) setSavedBotsShowAll(false);
          setSavedBotsExpanded(!savedBotsExpanded);
        }}>
          <Bx mr={1} style={{ fontSize: 12 }}>
            <FontAwesomeIcon icon={faTriangle} rotation={savedBotsOpen ? 180 : 90} />
          </Bx>
          <Typo variant="body2" component="span" style={{ fontSize: 14 }}>
            {searchInput ? `Gespeicherte Bots (${savedBotsVisible.length})` : 'Gespeicherte Bots'}
          </Typo>
        </Bx>
      )}
      {!!savedBotsVisible.length && (
        <Collapse expanded={savedBotsOpen} className={clsx(classes.collapse, !savedBotsOpen && classes.collapsed)}>
          <ListSidebar<IListItem>
            working={working}
            // A search lists every match; otherwise the newest few are previewed.
            items={searchInput || savedBotsShowAll ? savedBotsVisible : savedBotsVisible.slice(0, SAVED_BOTS_PREVIEW_COUNT)}
            renderItem={renderItem}
            top={<></>}
          >
          </ListSidebar>
          {!searchInput && savedBotsVisible.length > SAVED_BOTS_PREVIEW_COUNT && (
            <Bx
              className={clsx(classes.showMore)}
              onClick={() => setSavedBotsShowAll(!savedBotsShowAll)}
            >
              {savedBotsShowAll
                ? 'Weniger anzeigen'
                : `${savedBotsVisible.length - SAVED_BOTS_PREVIEW_COUNT} weitere anzeigen`}
            </Bx>
          )}
        </Collapse>
      )}
      {/* Gemerkt section (not in the bot rubric — it lists sessions only) */}
      {!botContainer && !searchInput && !!memorizedItems && !!memorizedItems.length && (
        <Bx display="flex" justifyContent="flex-start" alignItems="center" className={clsx(classes.collector, botSavedItems.length > 0 && classes.collectorbottom)} onClick={() => {
          // Reset the show-all preview when closing.
          if (memorizedExpanded) setMemorizedShowAll(false);
          memorizedExpanded ? setMemorizedExpanded(false) : setMemorizedExpanded(true);
        }}>
          <Bx mr={1} style={{ fontSize: 12 }}>
            <FontAwesomeIcon icon={faTriangle} rotation={memorizedExpanded ? 180 : 90} />
          </Bx>
          <Typo variant="body2" component="span" style={{ fontSize: 14 }}>
            Gemerkt
          </Typo>
        </Bx>
      )}
      {!botContainer && !!memorizedItems && !!memorizedItems.length && (
      <Collapse expanded={memorizedExpanded} className={clsx(classes.collapse, !memorizedExpanded && classes.collapsed)}>
        <ListSidebar<IListItem>
          working={working}
          items={memorizedShowAll ? memorizedItems : memorizedItems.slice(0, COLLAPSED_PREVIEW_COUNT)}
          renderItem={renderItem}
          top={<></>}
        >
          {working && _.range(3).map(i => (
            <SidebarListItemSkeleton key={`${i}_skeleton`} />
          ))}
          {!working && memorizedItems?.length === 0 &&
            <SidebarListItemEmpty key={'0_empty'} />
          }
        </ListSidebar>
        {memorizedItems.length > COLLAPSED_PREVIEW_COUNT && (
          <Bx
            className={clsx(classes.showMore)}
            onClick={() => setMemorizedShowAll(!memorizedShowAll)}
          >
            {memorizedShowAll
              ? 'Weniger anzeigen'
              : `${memorizedItems.length - COLLAPSED_PREVIEW_COUNT} weitere anzeigen`}
          </Bx>
        )}
      </Collapse>
      )}
      {!botContainer && !searchInput &&
        <Bx display="flex" justifyContent="flex-start" alignItems="center" className={clsx(classes.collector, classes.collectorbottom)} onClick={() => {
          // Reset the show-all preview when closing.
          if (defaultExpanded) setDefaultShowAll(false);
          defaultExpanded ? setDefaultExpanded(false) : setDefaultExpanded(true);
        }}>
          <Bx mr={1} style={{ fontSize: 12 }}>
            <FontAwesomeIcon icon={faTriangle} rotation={defaultExpanded ? 180 : 90} />
          </Bx>
          <Typo variant="body2" component="span" style={{ fontSize: 14 }}>
            Aktuell
          </Typo>
        </Bx>
      }
      {!botContainer && !searchInput && <Collapse expanded={defaultExpanded} className={clsx(classes.collapse, !defaultExpanded && classes.collapsed)}>
        <ListSidebar<IListItem>
          working={working}
          items={defaultShowAll
            ? defaultItems
            : defaultItems?.slice(0, COLLAPSED_PREVIEW_COUNT)}
          renderItem={renderItem}
          top={<></>}
        >
          {working && _.range(3).map(i => (
            <SidebarListItemSkeleton key={`${i}_skeleton`} />
          ))}
          {!working && defaultItems?.length === 0 &&
            <SidebarListItemEmpty key={'0_empty'} />
          }
        </ListSidebar>
        {defaultItems && defaultItems.length > COLLAPSED_PREVIEW_COUNT && (
          <Bx
            className={clsx(classes.showMore)}
            onClick={() => setDefaultShowAll(!defaultShowAll)}
          >
            {defaultShowAll
              ? 'Weniger anzeigen'
              : `${defaultItems.length - COLLAPSED_PREVIEW_COUNT} weitere anzeigen`}
          </Bx>
        )}
      </Collapse>}
      </>
    </SidebarWrapper>
  );
};
